/**
 * AuthContext
 * React context for authentication state and operations
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { IAuthProvider } from '@/core/interfaces'
import type { User, LoginDto, SignupDto } from '@/core/models'
import { AuthProviderFactory } from './AuthProviderFactory'

interface AuthContextValue {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginDto) => Promise<void>
  signup: (data: SignupDto) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
  authProvider?: IAuthProvider
}

export function AuthProvider({ children, authProvider }: AuthProviderProps) {
  const [provider] = useState<IAuthProvider>(
    authProvider || AuthProviderFactory.createFromEnvironment(),
  )
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      const authenticated = await provider.isAuthenticated()
      if (authenticated) {
        const currentUser = await provider.getCurrentUser()
        setUser(currentUser)
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error)
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials: LoginDto) => {
    try {
      const session = await provider.login(credentials)
      setUser(session.user)
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const signup = async (data: SignupDto) => {
    try {
      const session = await provider.signup(data)
      setUser(session.user)
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Signup failed:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await provider.logout()
      setUser(null)
      setIsAuthenticated(false)
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  const refreshUser = async () => {
    try {
      const currentUser = await provider.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
